/**
 * Practitioner formula builder values (PRA-020 to PRA-023). Diffing is pure so the Worker and the
 * clinic page agree on what a physician changed, without either reaching the catalog.
 */
import { z } from "zod";
import { isReviewEditChange, type ReviewEditChange, type ReviewEditReason } from "./admin.ts";
import { QUESTIONS, type IntakeAnswers } from "./intake.ts";

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

const StoredRationale = z.object({
  sentence: z.string(),
  findingIds: z.array(z.string()).optional(),
  ruleIds: z.array(z.string()).optional(),
  observations: z
    .array(
      z.object({
        code: z.string().optional(),
        value: z.number().optional(),
        collectedOn: z.string().nullable().optional(),
      }),
    )
    .optional(),
});

export type ClinicFormulaRationale = {
  readonly sentence: string;
  readonly findingIds: readonly string[];
  readonly ruleIds: readonly string[];
  readonly observations: readonly ClinicFormulaObservation[];
};

const EMPTY_RATIONALE: ClinicFormulaRationale = {
  sentence: "",
  findingIds: [],
  ruleIds: [],
  observations: [],
};

/** The FOR-030 rationale stored on a component, or empty fields when the JSON has none. */
export function clinicRationaleOf(rationaleJson: string): ClinicFormulaRationale {
  let parsed: unknown;

  try {
    parsed = JSON.parse(rationaleJson);
  } catch {
    return EMPTY_RATIONALE;
  }

  const read = StoredRationale.safeParse(parsed);

  if (!read.success) return EMPTY_RATIONALE;

  return {
    sentence: read.data.sentence,
    findingIds: read.data.findingIds ?? [],
    ruleIds: read.data.ruleIds ?? [],
    observations: (read.data.observations ?? []).flatMap((observation) =>
      observation.code !== undefined && observation.value !== undefined
        ? [
            {
              code: observation.code,
              value: observation.value,
              collectedOn: observation.collectedOn ?? null,
            },
          ]
        : [],
    ),
  };
}

export type ClinicFormulaStatus = "none" | "draft" | "in_review" | "approved" | "declined";

export type ClinicFormulaObservation = {
  readonly code: string;
  readonly value: number;
  readonly collectedOn: string | null;
};

export type ClinicFormulaIngredient = {
  readonly ingredientId: string;
  readonly name: string;
  readonly dose: number;
  readonly unit: string;
  readonly separateAddon: boolean;
  readonly isProteinBase: boolean;
  readonly sentence: string;
  readonly findingIds: readonly string[];
  readonly ruleIds: readonly string[];
  readonly observations: readonly ClinicFormulaObservation[];
};

export type ClinicFormulaCatalogItem = {
  readonly id: string;
  readonly name: string;
  readonly unit: string;
  readonly dailyMin: number;
  readonly dailyMax: number;
};

export type ClinicFormulaViolation = {
  readonly kind: string;
  readonly severity: "block" | "warn";
  readonly ingredientIds: readonly string[];
  readonly ruleId: string | null;
  readonly note: string;
};

export type ClinicFormulaIntake = {
  readonly dateOfBirth: string | null;
  readonly sex: "female" | "male" | null;
  readonly heightCm: number | null;
  readonly weightKg: number | null;
  readonly exerciseDaysPerWeek: number | null;
  readonly exerciseType: "none" | "strength" | "endurance" | "mixed" | "other" | null;
  readonly diet: "omnivore" | "pescatarian" | "vegetarian" | "vegan" | "other" | null;
  readonly goal: "general" | "energy" | "strength" | "weight" | "other" | null;
  readonly conditions: readonly string[];
  readonly medications: readonly string[];
  readonly allergies: readonly string[];
};

export type ClinicFormulaChangeSummary = {
  readonly reviewId: string;
  readonly acknowledged: boolean;
};

export type ClinicFormulaView = {
  readonly patientId: string;
  readonly patientName: string | null;
  readonly status: ClinicFormulaStatus;
  readonly formulaVersionId: string | null;
  readonly version: number | null;
  readonly licenceVerified: boolean;
  readonly intake: ClinicFormulaIntake;
  readonly ingredients: readonly ClinicFormulaIngredient[];
  readonly catalog: readonly ClinicFormulaCatalogItem[];
  readonly missingMarkers: readonly string[];
  readonly violations: readonly ClinicFormulaViolation[];
  readonly change: ClinicFormulaChangeSummary | null;
};

export type ClinicFormulaDiffLine = {
  readonly ingredientId: string;
  readonly name: string;
  readonly dose: number;
  readonly unit: string;
};

export type ClinicFormulaDiffEntry = {
  readonly ingredientId: string;
  readonly name: string;
  readonly change: ReviewEditChange;
  readonly fromDose: number | null;
  readonly toDose: number | null;
  readonly unit: string;
};

export type ClinicFormulaPhysicianEdit = {
  readonly ingredientId: string;
  readonly name: string;
  readonly change: ReviewEditChange;
  readonly fromDose: number | null;
  readonly toDose: number | null;
  readonly reasonCode: ReviewEditReason;
  readonly note: string | null;
};

export type ClinicFormulaChange = {
  readonly reviewId: string;
  readonly patientId: string;
  readonly patientName: string | null;
  readonly acknowledged: boolean;
  readonly notifiedAt: string | null;
  readonly submitted: readonly ClinicFormulaDiffLine[];
  readonly result: readonly ClinicFormulaDiffLine[];
  readonly diff: readonly ClinicFormulaDiffEntry[];
  readonly edits: readonly ClinicFormulaPhysicianEdit[];
};

export type ClinicFormulaEdit = {
  readonly ingredientId: string;
  readonly change: ReviewEditChange;
  readonly dose: number | null;
};

/** What changed between the practitioner's submitted formula and the physician's result. */
export function formulaDiff(
  submitted: readonly ClinicFormulaDiffLine[],
  result: readonly ClinicFormulaDiffLine[],
): readonly ClinicFormulaDiffEntry[] {
  const before = new Map(submitted.map((line) => [line.ingredientId, line]));
  const after = new Map(result.map((line) => [line.ingredientId, line]));

  const ids = [...new Set([...before.keys(), ...after.keys()])].toSorted((a, b) =>
    a.localeCompare(b),
  );

  const entries: ClinicFormulaDiffEntry[] = [];

  for (const id of ids) {
    const from = before.get(id);
    const to = after.get(id);

    if (from === undefined && to !== undefined) {
      entries.push({
        ingredientId: id,
        name: to.name,
        change: "added",
        fromDose: null,
        toDose: to.dose,
        unit: to.unit,
      });
    } else if (from !== undefined && to === undefined) {
      entries.push({
        ingredientId: id,
        name: from.name,
        change: "removed",
        fromDose: from.dose,
        toDose: null,
        unit: from.unit,
      });
    } else if (from !== undefined && to !== undefined && from.dose !== to.dose) {
      entries.push({
        ingredientId: id,
        name: to.name,
        change: "dose_changed",
        fromDose: from.dose,
        toDose: to.dose,
        unit: to.unit,
      });
    }
  }

  return entries;
}

export function parseClinicFormulaEdit(input: {
  readonly ingredientId?: string;
  readonly change?: string;
  readonly dose?: number | null;
}): ClinicFormulaEdit | null {
  const ingredientId = (input.ingredientId ?? "").trim();
  const change = input.change ?? "";

  if (ingredientId === "" || !isReviewEditChange(change)) return null;

  if (change === "removed") return { ingredientId, change, dose: null };
  const dose = input.dose;

  if (dose === undefined || dose === null || !Number.isFinite(dose) || dose <= 0) return null;

  return { ingredientId, change, dose };
}

const ClinicIntakeWrite = z.object({
  date_of_birth: QUESTIONS.date_of_birth.answer.optional(),
  sex: QUESTIONS.sex.answer.optional(),
  height_weight: QUESTIONS.height_weight.answer.optional(),
  exercise: QUESTIONS.exercise.answer.optional(),
  diet: QUESTIONS.diet.answer.optional(),
  goal: QUESTIONS.goal.answer.optional(),
  conditions: QUESTIONS.conditions.answer.optional(),
  medications: QUESTIONS.medications.answer.optional(),
  allergies: QUESTIONS.allergies.answer.optional(),
  supplements: QUESTIONS.supplements.answer.optional(),
  pregnancy: QUESTIONS.pregnancy.answer.optional(),
});

/** Intake fields the builder may write, or null when the body does not fit. */
export function parseClinicIntake(text: string): IntakeAnswers | null {
  let parsed: unknown;

  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }

  const read = ClinicIntakeWrite.safeParse(parsed);

  if (!read.success) return null;
  const out: Mutable<IntakeAnswers> = {};

  if (read.data.date_of_birth !== undefined) out.date_of_birth = read.data.date_of_birth;

  if (read.data.sex !== undefined) out.sex = read.data.sex;

  if (read.data.height_weight !== undefined) out.height_weight = read.data.height_weight;

  if (read.data.exercise !== undefined) out.exercise = read.data.exercise;

  if (read.data.diet !== undefined) out.diet = read.data.diet;

  if (read.data.goal !== undefined) out.goal = read.data.goal;

  if (read.data.conditions !== undefined) out.conditions = read.data.conditions;

  if (read.data.medications !== undefined) out.medications = read.data.medications;

  if (read.data.allergies !== undefined) out.allergies = read.data.allergies;

  if (read.data.supplements !== undefined) out.supplements = read.data.supplements;

  if (read.data.pregnancy !== undefined) out.pregnancy = read.data.pregnancy;

  return out;
}

export function clinicIntakeOf(
  answers: IntakeAnswers,
  dateOfBirth: string | null,
): ClinicFormulaIntake {
  return {
    dateOfBirth: answers.date_of_birth?.date ?? dateOfBirth,
    sex: answers.sex ?? null,
    heightCm: answers.height_weight?.heightCm ?? null,
    weightKg: answers.height_weight?.weightKg ?? null,
    exerciseDaysPerWeek: answers.exercise?.daysPerWeek ?? null,
    exerciseType: answers.exercise?.type ?? null,
    diet: answers.diet ?? null,
    goal: answers.goal ?? null,
    conditions: answers.conditions ?? [],
    medications: answers.medications ?? [],
    allergies: answers.allergies ?? [],
  };
}
